<template>
  <div class="modal-card" id="feedback">
    <header class="modal-card-head has-background-black-ter">
      <p class="modal-card-title has-text-light">
        <span>{{ $t('feedback') }}</span>
      </p>
      <button
        @click="closeModal()"
        :title="$t('close')"
        class="delete is-medium">
      </button>
    </header>
    <section class="modal-card-body has-background-dark has-text-light">
      <form>
        <b-field v-for="(ev, i) in events" grouped group-multiline :key="i" class="row">
          <b-field :label="$t('feedback.feedback_type')">
            <b-select
              class="datepicker-width"
              :disabled="ev.feedback_type === 'add' || ev.feedback_type === 'saved'"
              v-model="ev.feedback_type">
              <option v-if="!!ev.parcel_estimate_id" value="" selected>{{ $t('feedback.select') }}</option>
              <option v-if="!!ev.parcel_estimate_id" value="adjust">{{ $t('feedback.type.adjust') }}</option>
              <option v-if="!!ev.parcel_estimate_id" value="remove">{{ $t('feedback.type.remove') }}</option>
              <option v-if="!ev.parcel_estimate_id" value="add" selected>{{ $t('feedback.type.add') }}</option>
            </b-select>
          </b-field>
          <b-field :label="$t('feedback.event_type')">
            <b-select
              class="datepicker-width"
              :disabled="ev.feedback_type !== 'adjust' && ev.feedback_type !== 'add'"
              v-model="ev.estimation_type">
              <option v-for="(type, j) in event_types" :key="j" :value="(j + 1)">{{ type }}</option>
            </b-select>
          </b-field>
          <b-field :label="$t('feedback.start_date')">
            <b-datepicker
              class="datepicker-width"
              :placeholder="$t('feedback.select')"
              :date-formatter="date => fromDate(date, 'DD.MM.YYYY')"
              :month-names="months"
              :day-names="days"
              :first-day-of-week="1"
              :disabled="ev.feedback_type !== 'adjust' && ev.feedback_type !== 'add'"
              :min-date="minDate"
              :max-date="maxDate"
              :focused-date="minDate"
              v-model="ev.estimate_start_time"
              icon="calendar-today">
            </b-datepicker>
          </b-field>
          <b-field :label="$t('feedback.end_date')">
            <b-datepicker
              class="datepicker-width"
              :placeholder="$t('feedback.select')"
              :date-formatter="date => fromDate(date, 'DD.MM.YYYY')"
              :month-names="months"
              :day-names="days"
              :first-day-of-week="1"
              :disabled="ev.feedback_type !== 'adjust' && ev.feedback_type !== 'add'"
              :min-date="minDate"
              :max-date="maxDate"
              :focused-date="minDate"
              v-model="ev.estimate_end_time"
              icon="calendar-today">
            </b-datepicker>
          </b-field>
          <b-field :label="$t('feedback.comment')" expanded>
            <b-input
              class="comment-width comment-margin"
              :disabled="ev.feedback_type !== 'adjust' && ev.feedback_type !== 'add'"
              v-model="ev.comment">
            </b-input>
          </b-field>
          <b-field :label="$t('feedback.remove')">
            <b-button
              @click="removeEvent(i)"
              :disabled="!!ev.parcel_estimate_id || ev.feedback_type === 'saved'"
              :type="getRemoveButtonType(ev)"
              :icon-left="getIcon(ev)">
            </b-button>
          </b-field>
        </b-field>
      </form>
    </section>
    <footer class="modal-card-foot has-background-black-ter has-text-light">
      <span>
        <button
          class="button is-black"
          type="button"
          @click="openChartModal">
          {{ $t('view.chart') }}
        </button>
      </span>
      <span>
        <b-button
          @click="addEvent"
          type="is-success"
          icon-left="plus">
          {{ $t('feedback.new') }}
        </b-button>
        <button
          class="button is-info"
          type="button"
          @click="save()">
          {{ $t('save') }}
        </button>
      </span>
    </footer>
  </div>
</template>

<script>

import moment from 'moment'
import log from 'util/log'

export default {
  props: {
    parcel: {
      type: Object,
      default: null
    },
    chartBtnClick: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    return {
      events: this.parcel.events.length ?
        this.parcel.events
          .map(evt => {
            return Object.assign({}, this.$conf.api.endpoints.feedback, {
              parcel_id: this.parcel.parcel_id,
              parcel_estimate_id: evt.id,
              estimate_start_time: this.toDate(evt.s1product_end_time),
              estimate_end_time: this.toDate(evt.s1product_end_time),
              created_by: this.$store.getters['user']
            })
          }) :
        [Object.assign({}, this.$conf.api.endpoints.feedback, {
          feedback_type: 'add',
          parcel_id: this.parcel.parcel_id,
          created_by: this.$store.getters['user']
        })],
      event_types: ['Mowing', 'Ploughing', 'Tillage', 'Grazing', 'Other']
    }
  },
  computed: {
    months () {
      return [...Array(12).keys()].map(i => this.$t(`calendar.month${i}`))
    },
    days () {
      return [...Array(7).keys()].map(i => this.$t(`calendar.day${i}`))
    },
    minDate () {
      return moment(this.$conf.timebar.minDate).toDate()
    },
    maxDate () {
      return moment(this.$conf.timebar.maxDate).toDate()
    }
  },
  methods: {
    clear () {
      this.events = []
    },
    save () {
      this.saveFeedback(0)
    },
    getRemoveButtonType (ev) {
      if (ev.parcel_estimate_id) {
        return 'is-default'
      } else {
        return ev.feedback_type === 'saved' ? 'is-success' : 'is-danger'
      }
    },
    saveFeedback (i) {
      const row = Object.assign({}, this.events[i])
      // validate
      if (row.feedback_type === '' && i < this.events.length) {
        i += 1
        setTimeout(() => { this.saveFeedback(i) }, 100)
        return
      }
      if (this.validate(row)) {
        row.estimate_start_time = this.fromDate(row.estimate_start_time)
        row.estimate_end_time = this.fromDate(row.estimate_end_time)
        this.$store.dispatch('submitFeedback', row)
          .then(status => {
            this.events[i].feedback_type = 'saved'
            i += 1
            if (i < this.events.length) {
              setTimeout(() => { this.saveFeedback(i) }, 100)
            } else {
              log('success', this.$t('feedback.success'))
              setTimeout(() => { this.closeModal() }, 1000)
            }
          }).catch(err => {
            console.error(err)
            log('danger', this.$t('feedback.error'))
          })
      } else {
        log('danger', this.$t('feedback.validate.error'))
      }
    },
    validate (row) {
      let valid = true
      switch (row.feedback_type) {
        case 'add':
        case 'adjust':
          valid = (
            row.estimate_start_time &&
            row.estimate_end_time &&
            row.estimate_start_time <= row.estimate_end_time
          )
          break
        case 'remove':
          break
        default:
          valid = false
      }
      return valid
    },
    closeModal () {
      this.clear()
      this.$parent.close()
    },
    fromDate (date, format = 'YYYY-MM-DD') {
      return date ? moment(date).format(format) : null
    },
    toDate (ts) {
      return ts ? new Date(ts) : null
    },
    addEvent () {
      this.events.push(Object.assign({}, this.$conf.api.endpoints.feedback, {
        feedback_type: 'add',
        parcel_id: this.parcel.parcel_id,
        created_by: this.$store.getters['user']
      }))
    },
    removeEvent (i) {
      if (this.events[i].parcel_estimate_id) {
        this.events[i].feedback_type = 'remove'
      } else {
        this.events.splice(i, 1)
      }
      if (!this.events.length) {
        this.addEvent()
      }
    },
    openChartModal (e) {
      this.closeModal()
      this.chartBtnClick(e)
    },
    getIcon (ev) {
      switch (ev.feedback_type) {
        case 'remove':
          return 'delete-forever'
          break
        case 'saved':
          return 'check'
          break
        default:
          return 'delete'
      }
      ev.feedback_type === 'remove' ? 'delete-forever' : 'delete'
    }
  }
}
</script>

<style lang="stylus">
  #feedback
    .modal-card-body
      overflow: auto
      max-height: calc(100vh - 180px)
    .row
      padding: 12px 0
      border-bottom: 1px solid #a7a7a7
      &:last-child
        border-bottom: 0
      .field:last-child
        button
          margin-right: 25px
          span
            margin-right: -.2rem
    .datepicker-width
      width: 130px
      select
        width: 130px
    .field.is-grouped:first-child .field
      margin-bottom: 0
    .datepicker .dropdown.is-disabled
      opacity: 1
    select[disabled], input[disabled]
      background: #bbb
  @media (min-width: 768px)
    #feedback
      .row
        border-bottom: 0
        &:not(:first-child) label
          display: none
        .field
          margin-bottom: 0
  @media (min-width: 1000px)
    #feedback
      width: 1000px
</style>
